/*
  Shared React Query cache helpers for the WhatsApp Inbox.
  Used by both the message thread (optimistic sends / mark read) and the SSE
  stream (live conversation + message updates) so merge/dedupe logic stays in one place.
*/

export const CONVERSATIONS_KEY = 'inboxConversations';
export const MESSAGES_KEY = 'inboxMessages';
export const MESSAGE_LIMIT = 50;

export function messagesQueryKey(phone) {
  return [MESSAGES_KEY, phone];
}

/** Backend order: unread conversations first, then most-recent activity. */
export function sortConversations(list) {
  return list.slice().sort((a, b) => {
    const aUnread = a.unread_count > 0 ? 1 : 0;
    const bUnread = b.unread_count > 0 ? 1 : 0;
    if (aUnread !== bUnread) return bUnread - aUnread;
    return new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0);
  });
}

function messageMatches(a, b) {
  if (a == null || b == null) return false;
  if (b.id != null && a.id === b.id) return true;
  if (b.provider_message_id && a.provider_message_id === b.provider_message_id) return true;
  return false;
}

/* ------------------------------------------------------------------ */
/* Conversation list                                                  */
/* ------------------------------------------------------------------ */

export function clearUnreadInCache(queryClient, phone) {
  queryClient.setQueriesData({ queryKey: [CONVERSATIONS_KEY] }, (old) => {
    if (!old?.pages) return old;
    let removedUnreadMessages = 0;
    let removedUnreadConversations = 0;
    const pages = old.pages.map((page) => {
      const conversations = (page.conversations || []).map((conv) => {
        if (conv.phone_number === phone && conv.unread_count > 0) {
          removedUnreadMessages += conv.unread_count;
          removedUnreadConversations += 1;
          return { ...conv, unread_count: 0 };
        }
        return conv;
      });
      return { ...page, conversations };
    });
    if (pages[0]?.summary) {
      pages[0] = {
        ...pages[0],
        summary: {
          unread_conversations: Math.max(
            0,
            (pages[0].summary.unread_conversations || 0) - removedUnreadConversations
          ),
          unread_messages: Math.max(
            0,
            (pages[0].summary.unread_messages || 0) - removedUnreadMessages
          )
        }
      };
    }
    return { ...old, pages };
  });
}

export function updateListPreview(queryClient, phone, text) {
  const now = new Date().toISOString();
  queryClient.setQueriesData({ queryKey: [CONVERSATIONS_KEY] }, (old) => {
    if (!old?.pages) return old;
    const pages = old.pages.map((page) => ({
      ...page,
      conversations: (page.conversations || []).map((conv) =>
        conv.phone_number === phone
          ? {
              ...conv,
              last_message_preview: text,
              last_message_at: now,
              last_message_direction: 'outgoing'
            }
          : conv
      )
    }));
    return { ...old, pages };
  });
}

/**
 * Upsert a conversation row (from an SSE `conversation_updated` event), move it
 * to the top page, re-sort (unread-first, newest-first), and adjust the summary
 * by the unread delta. New conversations are inserted; existing ones are merged.
 */
export function upsertConversationInCache(queryClient, conv) {
  if (!conv?.phone_number) return;
  queryClient.setQueriesData({ queryKey: [CONVERSATIONS_KEY] }, (old) => {
    if (!old?.pages?.length) return old;

    let existing = null;
    const pagesWithoutConv = old.pages.map((page) => {
      const kept = [];
      (page.conversations || []).forEach((c) => {
        if (c.phone_number === conv.phone_number) existing = c;
        else kept.push(c);
      });
      return { ...page, conversations: kept };
    });

    const merged = { ...(existing || {}), ...conv };
    const pages = pagesWithoutConv.slice();
    const firstPage = pages[0];
    pages[0] = {
      ...firstPage,
      conversations: sortConversations([merged, ...(firstPage.conversations || [])])
    };

    if (pages[0].summary) {
      const oldUnread = existing?.unread_count || 0;
      const newUnread = merged.unread_count || 0;
      const deltaMessages = newUnread - oldUnread;
      const deltaConversations = (newUnread > 0 ? 1 : 0) - (oldUnread > 0 ? 1 : 0);
      pages[0] = {
        ...pages[0],
        summary: {
          unread_conversations: Math.max(
            0,
            (pages[0].summary.unread_conversations || 0) + deltaConversations
          ),
          unread_messages: Math.max(
            0,
            (pages[0].summary.unread_messages || 0) + deltaMessages
          )
        }
      };
    }
    return { ...old, pages };
  });
}

/* ------------------------------------------------------------------ */
/* Thread messages                                                    */
/* ------------------------------------------------------------------ */

export function addOptimisticMessage(queryClient, phone, tempId, text) {
  const optimistic = {
    _tempId: tempId,
    id: tempId,
    direction: 'outgoing',
    message_text: text,
    message_type: 'text',
    status: 'sending',
    provider_message_id: null,
    created_at: new Date().toISOString()
  };
  queryClient.setQueryData(messagesQueryKey(phone), (old) => {
    if (!old?.pages?.length) {
      return {
        pages: [
          {
            phone_number: phone,
            messages: [optimistic],
            pagination: { page: 1, limit: MESSAGE_LIMIT, total: 1 }
          }
        ],
        pageParams: [1]
      };
    }
    // Page 1 holds the newest batch, oldest -> newest, so the newest goes at the end.
    const pages = old.pages.slice();
    pages[0] = { ...pages[0], messages: [...(pages[0].messages || []), optimistic] };
    return { ...old, pages };
  });
}

/**
 * Optimistically append an outgoing media message. Mirrors the backend media
 * shape (message_type + message_text JSON snapshot + metadata.media) so the
 * existing thread renderer (parseRichMessage / MediaMessage) displays it. Pass a
 * local object URL as `localUrl` for an instant preview before the S3 URL exists.
 */
export function addOptimisticMedia(queryClient, phone, tempId, { file, type, localUrl }) {
  const optimistic = {
    _tempId: tempId,
    id: tempId,
    direction: 'outgoing',
    message_type: type,
    message_text: JSON.stringify({ type, filename: file?.name || '' }),
    status: 'sending',
    provider_message_id: null,
    created_at: new Date().toISOString(),
    metadata: {
      media: {
        kind: type,
        s3Url: localUrl,
        mime_type: file?.type || '',
        filename: file?.name || ''
      }
    }
  };
  queryClient.setQueryData(messagesQueryKey(phone), (old) => {
    if (!old?.pages?.length) {
      return {
        pages: [
          {
            phone_number: phone,
            messages: [optimistic],
            pagination: { page: 1, limit: MESSAGE_LIMIT, total: 1 }
          }
        ],
        pageParams: [1]
      };
    }
    // Page 1 holds the newest batch, oldest -> newest, so the newest goes at the end.
    const pages = old.pages.slice();
    pages[0] = { ...pages[0], messages: [...(pages[0].messages || []), optimistic] };
    return { ...old, pages };
  });
}

export function reconcileOptimisticMessage(queryClient, phone, tempId, saved) {
  queryClient.setQueryData(messagesQueryKey(phone), (old) => {
    if (!old?.pages) return old;
    const alreadyExists = old.pages.some((page) =>
      (page.messages || []).some((m) => m._tempId !== tempId && messageMatches(m, saved))
    );
    const pages = old.pages.map((page) => {
      let messages = page.messages || [];
      if (alreadyExists) {
        messages = messages.filter((m) => m._tempId !== tempId);
      } else {
        messages = messages.map((m) => (m._tempId === tempId ? { ...saved } : m));
      }
      return { ...page, messages };
    });
    return { ...old, pages };
  });
}

export function markOptimisticFailed(queryClient, phone, tempId) {
  queryClient.setQueryData(messagesQueryKey(phone), (old) => {
    if (!old?.pages) return old;
    const pages = old.pages.map((page) => ({
      ...page,
      messages: (page.messages || []).map((m) =>
        m._tempId === tempId ? { ...m, status: 'failed' } : m
      )
    }));
    return { ...old, pages };
  });
}

/**
 * Append a message from an SSE `message_created` event. No-ops if the thread
 * isn't cached yet (opening it will fetch fresh). Dedupes by id/provider_message_id,
 * and reconciles an agent's own outgoing echo against a pending optimistic bubble.
 */
export function appendIncomingMessage(queryClient, phone, message) {
  if (!phone || !message) return;
  const existing = queryClient.getQueryData(messagesQueryKey(phone));
  if (!existing?.pages?.length) return;

  const duplicate = existing.pages.some((page) =>
    (page.messages || []).some((m) => !m._tempId && messageMatches(m, message))
  );
  if (duplicate) return;

  queryClient.setQueryData(messagesQueryKey(phone), (old) => {
    if (!old?.pages?.length) return old;

    // Outgoing echo of the agent's own send: replace the matching optimistic bubble.
    if (message.direction === 'outgoing') {
      let replaced = false;
      const pages = old.pages.map((page) => {
        if (replaced) return page;
        const messages = page.messages || [];
        const idx = messages.findIndex(
          (m) => m._tempId && m.message_text === message.message_text
        );
        if (idx === -1) return page;
        replaced = true;
        const next = messages.slice();
        next[idx] = { ...message };
        return { ...page, messages: next };
      });
      if (replaced) return { ...old, pages };
    }

    // Newest message goes at the end of page 1 (oldest -> newest order).
    const pages = old.pages.slice();
    pages[0] = { ...pages[0], messages: [...(pages[0].messages || []), message] };
    return { ...old, pages };
  });
}
